import { ChevronLeftIcon, ChevronRightIcon, MinusIcon, PlusIcon } from '@/components/common/icons';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

/** How far one zoom step moves; the range keeps a page readable without leaving the column. */
export const ZOOM_STEP = 0.15;
export const ZOOM_MIN = 0.7;
export const ZOOM_MAX = 2;

interface DocumentToolbarProps {
  pageNumber: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  zoom: number;
  onZoomChange: (zoom: number) => void;
  onZoomFit: () => void;
}

const TOOL_BUTTON =
  'flex h-[30px] w-[30px] cursor-pointer items-center justify-center border-none bg-transparent text-muted hover:bg-surface hover:text-primary disabled:cursor-default disabled:opacity-50';

/**
 * The updated prototype's floating pill: page navigation and zoom together, sticky over the
 * bottom of the page column instead of living in a separate top or bottom bar.
 */
export const DocumentToolbar: FC<DocumentToolbarProps> = ({
  pageNumber,
  pageCount,
  onPageChange,
  zoom,
  onZoomChange,
  onZoomFit,
}) => {
  const { t } = useTranslation();
  return (
    <div className="pointer-events-none sticky bottom-4 z-10 flex justify-center px-4">
      <div className="pointer-events-auto flex items-center gap-[2px] border border-control bg-white p-1 shadow-[0_6px_20px_rgba(11,30,50,.14)]">
        <button
          type="button"
          className={TOOL_BUTTON}
          onClick={() => onPageChange(pageNumber - 1)}
          disabled={pageNumber <= 1}
          aria-label={t('common.previousPage')}
        >
          <ChevronLeftIcon size={14} />
        </button>
        <div className="min-w-[52px] text-center text-xs font-semibold text-ink" aria-live="polite">
          {t('review.toolbar.pageValue', { page: pageNumber, count: pageCount })}
        </div>
        <button
          type="button"
          className={TOOL_BUTTON}
          onClick={() => onPageChange(pageNumber + 1)}
          disabled={pageNumber >= pageCount}
          aria-label={t('common.nextPage')}
        >
          <ChevronRightIcon size={14} />
        </button>
        <div className="mx-1 h-5 w-px bg-control" aria-hidden="true" />
        <button
          type="button"
          className={TOOL_BUTTON}
          onClick={() => onZoomChange(zoom - ZOOM_STEP)}
          disabled={zoom <= ZOOM_MIN}
          aria-label={t('review.toolbar.zoomOut')}
        >
          <MinusIcon size={14} />
        </button>
        <button
          type="button"
          onClick={onZoomFit}
          title={t('review.toolbar.zoomFit')}
          className="min-w-[52px] cursor-pointer border-none bg-transparent px-1 text-xs font-semibold text-ink hover:text-primary"
        >
          {t('review.toolbar.zoomValue', { percent: Math.round(zoom * 100) })}
        </button>
        <button
          type="button"
          className={TOOL_BUTTON}
          onClick={() => onZoomChange(zoom + ZOOM_STEP)}
          disabled={zoom >= ZOOM_MAX}
          aria-label={t('review.toolbar.zoomIn')}
        >
          <PlusIcon size={14} />
        </button>
      </div>
    </div>
  );
};
